/**
 * Accident Benefit Definitions
 *
 * Uses builder functions for standard type/amount pairs.
 * Enum values provided via typeEnumValues parameter using OpenAPI types.
 */

import {
  addNotDisclosed,
  addNotDisclosedAndNotOffered,
  BaseBenefitPlanPolicyPeriod,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  NotDisclosed,
  NotOffered,
  RateGuaranteeTypeEnum,
  RfpAccidentBenefitTypeEnum,
  RfpAccidentCoverageTypeEnum,
  RfpAccidentMaxBenefitTypeEnum,
  RfpAccidentSpecificBenefitTypeEnum,
  RfpInclusionStatusEnum,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  buildTypeAmountBenefitPair,
  buildAgeReductionScheduleBenefitPair,
  buildCommissionBenefits,
} from "../shared/definitions/builders";
import * as sharedCellSpecs from "../shared/cellSpecs/common";

const INCLUDED_IN_LUMP_SUM_DISABLE_VALUES = addNotDisclosed([
  "notIncluded",
  "includedInLumpSumBenefitPayout",
]);

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  coverageType: {
    label: "Coverage Type",
    description:
      "Indicates when the accident event may occur to be eligible for coverage (hours covered)",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpAccidentCoverageTypeEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "hospitalAdmission",
    typeLabel: "Hospital Admission",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpAccidentSpecificBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for hospital admission",
    amountDescription: "Hospital admission benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "icuAdmission",
    typeLabel: "ICU Admission",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    amountLabel: "ICU Admission Amount",
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for ICU admission",
    amountDescription: "ICU admission benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "dailyHospitalConfinement",
    typeLabel: "Daily Hospital Confinement",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpAccidentSpecificBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for daily hospital confinement",
    amountDescription: "Daily hospital confinement benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "dailyIcuConfinement",
    typeLabel: "Daily ICU Confinement",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for daily ICU confinement",
    amountDescription: "Daily ICU confinement benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "ambulanceAir",
    typeLabel: "Ambulance - Air",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpAccidentSpecificBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for air ambulance",
    amountDescription: "Air ambulance benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "ambulanceGround",
    typeLabel: "Ambulance - Ground",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpAccidentSpecificBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for ground ambulance",
    amountDescription: "Ground ambulance benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "emergencyTreatment",
    typeLabel: "Emergency Treatment",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpAccidentSpecificBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for emergency treatment",
    amountDescription: "Emergency treatment benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "urgentCare",
    typeLabel: "Urgent Care Visit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpAccidentSpecificBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for urgent care visit",
    amountDescription: "Urgent care visit benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "initialPhysicianOfficeVisit",
    typeLabel: "Initial Physician Office Visit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpAccidentSpecificBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount carrier will cover as benefit for initial physician office visit",
    amountDescription: "Initial physician office visit benefit dollar amount",
    amountDefaultValue: 1,
    disabledValues: INCLUDED_IN_LUMP_SUM_DISABLE_VALUES,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "dislocationsMaximum",
    typeLabel: "Dislocations Maximum",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentMaxBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    amountLabel: "Dislocations Maximum Amount",
    typeDescription: "Max benefit amount for dislocations",
    amountDescription: "Dislocations maximum benefit dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "fracturesMaximum",
    typeLabel: "Fractures Maximum",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentMaxBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    amountLabel: "Fractures Maximum Amount",
    typeDescription: "Max benefit amount for fractures",
    amountDescription: "Fractures maximum benefit dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "lacerationsMaximum",
    // The presentation's "Laceration Maximum Benefit" row is this benefit: the
    // AI-Dox field of that name feeds the v5 `lacerationsMaximum` this row
    // reads (canonical/v5/mappers/aidocuments/rfp/accident.go).
    mpConfigKey: "laceration_maximum_benefit",
    typeLabel: "Laceration Maximum",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentMaxBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates the maximum benefit amount available for lacerations",
    amountDescription: "Lacerations maximum benefit dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "coma",
    mpConfigKey: "coma",
    typeLabel: "Coma",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentMaxBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Indicates the benefit amount available for a coma",
    amountDescription: "Coma benefit dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "majorDiagnosticExamXray",
    mpConfigKey: "major_diagnostic_exam_x_ray",
    typeLabel: "Major Diagnostic Exam - X-Ray",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentMaxBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Indicates the benefit amount available for an x-ray",
    amountDescription: "X-ray benefit dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "majorDiagnosticExamMri",
    mpConfigKey: "major_diagnostic_exam_mri",
    typeLabel: "Major Diagnostic Exam - MRI",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpAccidentMaxBenefitTypeEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Indicates the benefit amount available for an MRI",
    amountDescription: "MRI benefit dollar amount",
    amountDefaultValue: 1,
  }),
  childOrganizedSportsEnhancement: {
    label: "Child Organized Sport Enhancement",
    description: "Indicates if child organized sports enhancement is included",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  portability: {
    label: "Portability",
    description:
      "Indicates if policy holder can continue policy after separation of employment",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "wellnessBenefit",
    typeLabel: "Wellness Benefit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpAccidentSpecificBenefitTypeEnum,
    ).filter((v) =>
      ["dollarAmount", "notIncluded", NotDisclosed, NotOffered].includes(v),
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription:
      "Indicates dollar amount of available reimbursement for member completing wellness initiative(s)",
    amountDescription: "Wellness benefit dollar amount",
    amountDefaultValue: 1,
  }),
  ...buildAgeReductionScheduleBenefitPair({
    typeEnumValues: addNotDisclosedAndNotOffered([
      "percentageAtAge",
      "notIncluded",
    ]),
  }),
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Indicates if annual open enrollment is included in the plan",
    cell: sharedCellSpecs.annualOpenEnrollmentType,
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  volumeIllustratedFrom: mpOnlyRow(
    "Volume illustrated from _",
    "volume_illustrated_from",
  ),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),

  // Extended (non-v5) fields (CD-390). Backend fields are plain strings for
  // now, not yet enums, so both are text rather than dropdowns.
  // Unlike its sibling extended fields, the source spreadsheet's row for
  // this field has no Data Type or value list (CD-2325 investigation found
  // no approved dropdown values to source from) — stays text until one is
  // confirmed.
  accidentalDeathDismemberment: {
    mpOnly: true,
    label: "Accidental Death & Dismemberment",
    mpConfigKey: "accidental_death_dismemberment",
    description: "Accidental death and dismemberment benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  // Unlike its sibling extended field above, eligibilityHours has a
  // confirmed dropdown value list (CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
};
